import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import { useNavigate } from 'react-router-dom';
import RisqueListData from './risqueListData';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    name: 500, description: 800
};

const RisqueList = React.memo(({ scrollRef, risques, sortBy, setSortBy, setOffset, filteredRisquesCount, setRisquesFetched }) => {

    const navigate = useNavigate();

    const [risqueColumnWidth, setRisqueColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('risqueColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const tableHeadRef = useRef(null);

    const handleColumnWidthChange = (value, columnKey) => {
        setRisqueColumnWidth({ ...risqueColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('risqueColumnWidth', JSON.stringify(risqueColumnWidth));
    }, [risqueColumnWidth]);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredRisquesCount, setRisquesFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setRisquesFetched, filteredRisquesCount, 70, 280);

    const handleRisqueClick = useCallback((risqueId) => navigate(`risque_details/${risqueId}`), [navigate]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const Row = ({ index, style, data }) => {
        const { risques, handleRisqueClick } = data;
        const risque = index === 0 ? risques[0] : risques[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <RisqueListData
                    risque={risque}
                    columnWidth={risqueColumnWidth}
                    handleRisqueClick={handleRisqueClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table'>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Nom'
                    columnKey='name'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={true}
                    columnWidth={risqueColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Description'
                    columnKey='description'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    columnWidth={risqueColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter' width="50px" />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredRisquesCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredRisquesCount}
                    itemSize={70}
                    itemData={{ risques, handleRisqueClick }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default RisqueList;